import React, { useEffect } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import Spacer from "@/components/layout/Spacer";
import MobileInput from "@/components/MobileInput";
import { graphql } from "@/gql";
import { Gender, PersonalInfoSectionFragment } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { formatISODate, parseDate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { apply } from "@/utils/functions";
import { humanize } from "@/utils/string";

import {
  clientProfilePageQuery,
  updateClientProfileMutation,
} from "../../../ClientProfilePage";

type EditPersonalInfoModalType = {
  visible: boolean | undefined;
  onClose: () => void;
  showSnackBar: (isSuccessful: boolean, message?: string) => void;
  profile: PersonalInfoSectionFragment["profile"];
  email?: string;
  loading: boolean;
  isRegistered: boolean;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100vh",
    width: "100%",
    display: "grid",
    placeItems: "center",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(2, 2),
    width: "40%",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: theme.spacing(0, 4),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  save: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    marginBottom: "24px",
    marginTop: "28px",
  },
  error: {
    fontSize: "12px",
    textAlign: "center",
    color: "#963832",
  },
  mobileInputRoot: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
  },
  red: {
    color: "red",
  },
}));

const validationSchema = yup
  .object()
  .shape({
    firstName: yup.string().label("First name").required(),
    lastName: yup.string().label("Last name").required(),
    middleName: yup.string().label("Middle name").notRequired().nullable(),
    countryCode: yup.string(),
    mobileNum: yup
      .string()
      .nullable()
      .test("isValidPhoneNumber", "Invalid Mobile Number", function (value) {
        const defaultCountryCode = this.parent.countryCode;
        if (!value) return true;
        return isValidPhoneNumber(value, defaultCountryCode);
      }),
    landlineNum: yup
      .string()
      .nullable()
      .test("isValidPhoneNumber", "Invalid Landline Number", function (value) {
        const defaultCountryCode = this.parent.countryCode;
        if (!value) return true;
        return isValidPhoneNumber(value, defaultCountryCode);
      }),
    dateOfBirth: yup.date().label("Date of birth").nullable(),
    occupation: yup.string().label("Occupation").notRequired().nullable(),
    country: yup.string().label("Country").notRequired().nullable(),
    shouldInvite: yup.boolean().label("Checkbox"),
    isRegistered: yup.boolean(),
    email: yup.string().email().label("Email").when("isRegistered", {
      is: false,
      then: yup.string().required(),
    }),
    gender: yup.mixed().oneOf(Object.values(Gender)).required(),
  })
  .defined();

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    width: "100%",
    marginTop: "2px",
  },
});

const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.primary,
    width: "132px",
    height: "52px",
    "&:hover": {
      backgroundColor: colors.primary,
    },
  },
});

export type FormValues = yup.InferType<typeof validationSchema>;

const countriesQuery = graphql(`
  query Countries {
    countries {
      alpha2
      isoLongName
      isoShortName
    }
  }
`);

const EditPersonalInfoModal: React.FC<EditPersonalInfoModalType> = ({
  visible,
  onClose,
  showSnackBar,
  profile,
  email,
  loading,
  isRegistered,
}) => {
  const { classes } = useStyles();
  const { data, loading: countriesLoading } = useQuery(countriesQuery);
  const { clientId } = useParams<{ clientId: string }>();
  const modalVisibility = visible !== undefined ? visible : false;
  const [updateClientProfile] = useMutation(updateClientProfileMutation);
  const countries = data?.countries ?? [];

  const initialValues = {
    firstName: profile?.firstName ?? "",
    middleName: profile?.middleName ?? "",
    lastName: profile?.lastName ?? "",
    mobileNum: profile?.mobileNumber,
    landlineNum: profile?.landlineNumber,
    dateOfBirth: apply(profile?.dateOfBirth, parseDate),
    occupation: profile?.occupation,
    country: profile?.country ?? "",
    gender: profile?.gender ?? Gender.Male,
    shouldInvite: false,
    isRegistered: isRegistered,
    email: email,
  };

  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    mode: "onBlur",
    reValidateMode: "onSubmit",
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (!loading) reset(initialValues);
  }, [loading, profile]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const patch = {
      firstName: formValues?.firstName,
      middleName: formValues?.middleName,
      lastName: formValues?.lastName,
      mobileNumber: formValues?.mobileNum === "" ? null : formValues?.mobileNum,
      landlineNumber:
        formValues?.landlineNum === "" ? null : formValues?.landlineNum,
      dateOfBirth: apply(formValues.dateOfBirth, formatISODate),
      country: formValues?.country,
      occupation: formValues?.occupation,
      gender: formValues?.gender,
    };
    try {
      await updateClientProfile({
        variables: {
          input: {
            clientId: clientId,
            shouldInvite: formValues?.shouldInvite,
            newEmail: formValues?.email,
            patch: patch,
          },
        },
        refetchQueries: [
          { query: clientProfilePageQuery, variables: { id: clientId ?? "" } },
        ],
      });
      showSnackBar(true);
      onClose();
    } catch (e) {
      showSnackBar(false, displayError(e));
    }
  };

  const RequiredIndicator = (): JSX.Element => (
    <span className={classes.red}>*</span>
  );

  return (
    <Modal open={modalVisibility} onClose={onClose} className={classes.root}>
      <Box className={classes.modalBody}>
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalHeaderText}>
            Edit Personal Information
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box m={4}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        First Name <RequiredIndicator />
                      </Typography>
                      <StyledTextField {...field} />
                      <Typography className={classes.error} variant="subtitle1">
                        {errors.firstName?.message}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={4}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        Last Name <RequiredIndicator />
                      </Typography>
                      <StyledTextField {...field} />
                      <Typography className={classes.error} variant="subtitle1">
                        {errors.lastName?.message}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={4}>
                <Controller
                  name="middleName"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        Middle Name
                      </Typography>
                      <StyledTextField {...field} />
                      <Typography className={classes.error} variant="subtitle1">
                        {errors.middleName?.message}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
            {!isRegistered && (
              <>
                <Spacer y={4} />
                <Controller
                  name="email"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        Email <RequiredIndicator />
                      </Typography>
                      <StyledTextField {...field} />
                      <Typography className={classes.error} variant="subtitle1">
                        {errors.email?.message}
                      </Typography>
                    </Box>
                  )}
                />
              </>
            )}
            <Spacer y={4} />
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <FormLabel>
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Mobile Number
                    </Typography>
                    <MobileInput
                      classes={{ root: classes.mobileInputRoot }}
                      value={getValues("mobileNum") ?? ""}
                      error={!!errors.mobileNum?.message}
                      helperText={errors.mobileNum?.message ?? " "}
                      onChange={(e) =>
                        setValue("mobileNum", e as unknown as string)
                      }
                      numberType="mobile"
                    />
                  </Box>
                </FormLabel>
              </Grid>
              <Grid item xs={6}>
                <Box className={classes.fields}>
                  <Typography variant="subtitle1" color="textPrimary">
                    Landline Number
                  </Typography>
                  <MobileInput
                    classes={{ root: classes.mobileInputRoot }}
                    value={getValues("landlineNum") ?? ""}
                    error={!!errors.landlineNum?.message}
                    helperText={!!errors.landlineNum?.message ?? " "}
                    onChange={(e) =>
                      setValue("landlineNum", e as unknown as string)
                    }
                    numberType="telephone"
                  />
                </Box>
              </Grid>
            </Grid>
            <Spacer y={4} />
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <FormLabel>
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Date of birth
                    </Typography>
                    <Controller
                      control={control}
                      name="dateOfBirth"
                      render={({ field, fieldState }) => (
                        <DatePicker
                          {...field}
                          slotProps={{
                            textField: {
                              name: field.name,
                              fullWidth: true,
                              error: !!fieldState?.error,
                              helperText: fieldState?.error?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </FormLabel>
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        Gender
                      </Typography>

                      <StyledTextField select {...field}>
                        <MenuItem value={Gender.Male}>
                          {humanize(Gender.Male)}
                        </MenuItem>
                        <MenuItem value={Gender.Female}>
                          {humanize(Gender.Female)}
                        </MenuItem>
                      </StyledTextField>
                      <Typography className={classes.error} variant="subtitle1">
                        {errors.country?.message}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
            <Spacer y={4} />
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <FormLabel>
                  <Box className={classes.fields}>
                    <Typography variant="subtitle1" color="textPrimary">
                      Occupation
                    </Typography>
                    <StyledTextField
                      placeholder="Enter occupation"
                      {...register("occupation")}
                    />
                    <Typography className={classes.error} variant="subtitle1">
                      {errors.middleName?.message}
                    </Typography>
                  </Box>
                </FormLabel>
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="country"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <Box className={classes.fields}>
                      <Typography variant="subtitle1" color="textPrimary">
                        Country
                      </Typography>

                      <StyledTextField
                        select
                        {...field}
                        disabled={countriesLoading}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.alpha2} value={country.alpha2}>
                            {country.isoShortName}
                          </MenuItem>
                        ))}
                      </StyledTextField>
                      <Typography className={classes.error} variant="subtitle1">
                        {errors.country?.message}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
            <Spacer y={4} />
            <Controller
              name="shouldInvite"
              control={control}
              render={({ field }) => (
                <Box display="flex" alignItems="center">
                  <Checkbox {...field} color="primary" />
                  <Typography variant="subtitle1" color="textPrimary">
                    Send account activation via email
                  </Typography>
                </Box>
              )}
            />
            <div className={classes.save}>
              <StyledSaveButton type="submit" disabled={!isValid}>
                Update
              </StyledSaveButton>
            </div>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default EditPersonalInfoModal;
